import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

type DialogType = 'warning' | 'error' | 'info';

export interface TypedConfirmDialogData {
  title: string;
  message: string;
  /** Optional bullet-point details shown beneath the main message */
  details?: string[];
  /** Optional compact overflow label with hover text to keep modal content size bounded. */
  overflowHoverLabel?: string;
  /** Full hover text shown when overflowHoverLabel is present. */
  overflowHoverText?: string;
  type: DialogType;
  /** Phrase the user must type to enable the primary confirm button */
  confirmPhrase: string;
  /** Placeholder hint shown inside the text input */
  confirmPhrasePlaceholder?: string;
  /** Label for the primary confirm button (default: "Confirm") */
  confirmButtonText?: string;
  /** Label for the cancel button (default: "Cancel") */
  cancelText?: string;
  /**
   * Optional in-dialog summary shown when the primary phrase is matched.
   * Use this to clarify exactly what the primary action will do before the
   * user clicks the button.
   */
  confirmSummary?: string;
  /**
   * If provided, a secondary action button appears alongside the primary.
   * The secondary button unlocks when the user types `secondaryOption.phrase`.
   * Phrase is REQUIRED so each button has its own typed gate.
   */
  secondaryOption?: {
    /** Exact phrase the user must type to enable this button. */
    phrase: string;
    buttonText: string;
    /** Summary shown inside the dialog when this phrase is matched. */
    confirmSummary?: string;
    /** Extra CSS class on the secondary button, e.g. 'btn-danger-red'. */
    buttonColorClass?: string;
  };
}

export interface TypedConfirmDialogResult {
  confirmed: boolean;
  selectedOption: 'primary' | 'secondary' | null;
}

interface TypeStyling {
  iconClass: string;
  iconColor: string;
  headerBorderColor: string;
}

@Component({
  selector: 'app-typed-confirm-dialog',
  templateUrl: './typed-confirm-dialog.component.html',
  styleUrls: ['./typed-confirm-dialog.component.css']
})
export class TypedConfirmDialogComponent implements OnInit {
  typedInput = '';

  private readonly typeStylingMap: Record<DialogType, TypeStyling> = {
    warning: {
      iconClass:         'fa fa-exclamation-triangle',
      iconColor:         '#ff9800',
      headerBorderColor: '#ff9800'
    },
    error: {
      iconClass:         'fa fa-times-circle',
      iconColor:         '#f44336',
      headerBorderColor: '#f44336'
    },
    info: {
      iconClass:         'fa fa-info-circle',
      iconColor:         '#2196f3',
      headerBorderColor: '#2196f3'
    }
  };

  constructor(
    public dialogRef: MatDialogRef<TypedConfirmDialogComponent, TypedConfirmDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: TypedConfirmDialogData
  ) {}

  ngOnInit(): void {
    this.data.confirmButtonText = this.data.confirmButtonText ?? 'Confirm';
    this.data.cancelText        = this.data.cancelText        ?? 'Cancel';
    // Show hint for both phrases when a secondary option is present.
    if (this.data.secondaryOption?.phrase) {
      this.data.confirmPhrasePlaceholder = this.data.confirmPhrasePlaceholder ??
        `Type "${this.data.confirmPhrase}" or "${this.data.secondaryOption.phrase}"`;
    } else {
      this.data.confirmPhrasePlaceholder = this.data.confirmPhrasePlaceholder ??
        `Type "${this.data.confirmPhrase}" to confirm`;
    }
  }

  get styling(): TypeStyling {
    return this.typeStylingMap[this.data.type];
  }

  get isConfirmEnabled(): boolean {
    return this.typedInput === this.data.confirmPhrase;
  }

  get isSecondaryEnabled(): boolean {
    return !!(this.data.secondaryOption && this.typedInput === this.data.secondaryOption.phrase);
  }

  /** The summary panel text to display, or null when no phrase has been matched. */
  get activeSummary(): string | null {
    if (this.isSecondaryEnabled) return this.data.secondaryOption?.confirmSummary ?? null;
    if (this.isConfirmEnabled)   return this.data.confirmSummary ?? null;
    return null;
  }

  /** Which variant is currently active — drives icon and colour of the summary panel. */
  get activeSummaryVariant(): 'primary' | 'secondary' | null {
    if (this.isSecondaryEnabled) return 'secondary';
    if (this.isConfirmEnabled)   return 'primary';
    return null;
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false, selectedOption: null });
  }

  onConfirm(): void {
    if (!this.isConfirmEnabled) return;
    this.dialogRef.close({ confirmed: true, selectedOption: 'primary' });
  }

  onSecondary(): void {
    if (!this.isSecondaryEnabled) return;
    this.dialogRef.close({ confirmed: true, selectedOption: 'secondary' });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.isConfirmEnabled) {
      this.onConfirm();
    }
  }
}
